import React, { useState } from "react";
import { motion } from "framer-motion";
import { Bot, CheckCircle2, XCircle, AlertCircle, FileText, ChevronDown } from "lucide-react";
import { Card, Badge, Alert, Collapsible, CollapsibleTrigger, CollapsibleContent } from "../GraderUI";
import IndexabilityWarning from "./IndexabilityWarning";

const botDisplayNames = {
	GPTBot: "OpenAI GPTBot",
	PerplexityBot: "Perplexity",
	ClaudeBot: "Anthropic Claude",
	GoogleBot: "Google",
	"anthropic-ai": "Anthropic AI",
	"cohere-ai": "Cohere",
	Bytespider: "ByteDance",
};

const getCitationRateClass = (rate) => {
	if (rate >= 50) return "citation-rate--high";
	if (rate >= 30) return "citation-rate--medium";
	return "citation-rate--low";
};

const getScoreTextClass = (score) => {
	if (score >= 70) return "bot-crawlability__score-value--good";
	if (score >= 40) return "bot-crawlability__score-value--fair";
	return "bot-crawlability__score-value--poor";
};

const getScoreCardClass = (score) => {
	if (score >= 70) return "bot-crawlability__score-card--good";
	if (score >= 40) return "bot-crawlability__score-card--fair";
	return "bot-crawlability__score-card--poor";
};

export default function BotCrawlabilitySection({ data, indexabilityData, citationData }) {
	const [showRobotsTxt, setShowRobotsTxt] = useState(false);

	if (!data) {
		return null;
	}

	const {
		bot_access,
		sitemap_present,
		sitemap_url,
		crawlability_score,
		robots_txt_present,
		raw_robots_txt,
		default_rule,
	} = data;

	const blockedBots = Object.entries(bot_access)
		.filter(([, botInfo]) => botInfo.status === "blocked")
		.map(([bot]) => botDisplayNames[bot] || bot);

	const getCitationRate = (botName) => {
		if (!citationData?.per_provider) return null;

		const providerMap = {
			GPTBot: "openai",
			GoogleBot: "gemini",
			ClaudeBot: "claude",
		};

		const provider = providerMap[botName];
		if (!provider) return null;

		const providerData = citationData.per_provider[provider];
		return providerData ? Math.round(providerData.citation_rate * 100) : null;
	};

	const getBotStatusBadge = (botInfo) => {
		const { status, source } = botInfo;

		if (status === "allowed" && source === "explicit") {
			return (
				<Badge className="bot-crawlability__bot-badge bot-crawlability__bot-badge--allowed-explicit">
					<CheckCircle2 className="bot-crawlability__bot-badge-icon" />
					Explicitly Allowed
				</Badge>
			);
		}
		if (status === "allowed" && source === "default") {
			return (
				<Badge className="bot-crawlability__bot-badge bot-crawlability__bot-badge--allowed-default">
					<CheckCircle2 className="bot-crawlability__bot-badge-icon" />
					Allowed via Default
				</Badge>
			);
		}
		if (status === "blocked" && source === "explicit") {
			return (
				<Badge className="bot-crawlability__bot-badge bot-crawlability__bot-badge--blocked-explicit">
					<XCircle className="bot-crawlability__bot-badge-icon" />
					Explicitly Blocked
				</Badge>
			);
		}
		if (status === "blocked" && source === "default") {
			return (
				<Badge className="bot-crawlability__bot-badge bot-crawlability__bot-badge--blocked-default">
					<XCircle className="bot-crawlability__bot-badge-icon" />
					Blocked via Default
				</Badge>
			);
		}

		return null;
	};

	const getRobotsTxtFix = (blockedBot) => {
		const botUserAgents = {
			"OpenAI GPTBot": "GPTBot",
			Perplexity: "PerplexityBot",
			"Anthropic Claude": "ClaudeBot",
			Google: "Googlebot",
			"Anthropic AI": "anthropic-ai",
			Cohere: "cohere-ai",
			ByteDance: "Bytespider",
		};

		const userAgent = botUserAgents[blockedBot] || blockedBot;
		return `User-agent: ${userAgent}\nAllow: /`;
	};

	const primaryBots = ["GPTBot", "ClaudeBot", "PerplexityBot"];
	const missingExplicitRules = primaryBots.filter(
		(bot) => bot_access[bot]?.source === "default" && bot_access[bot]?.status === "allowed"
	);

	return (
		<motion.div
			className="bot-crawlability"
			initial={{ opacity: 0, y: 20 }}
			animate={{ opacity: 1, y: 0 }}
		>
			<div className="bot-crawlability__header">
				<div className="bot-crawlability__header-icon-wrap">
					<Bot className="bot-crawlability__header-icon" />
				</div>
				<div>
					<h2 className="bot-crawlability__title">Bot Crawlability</h2>
					<p className="bot-crawlability__subtitle">
						AI crawler access permissions and sitemap status
					</p>
				</div>
			</div>

			<Card className={`bot-crawlability__score-card ${getScoreCardClass(crawlability_score)}`}>
				<div className="bot-crawlability__score-row">
					<div>
						<p className="bot-crawlability__score-label">Overall Crawlability Score</p>
						<p className={`bot-crawlability__score-value ${getScoreTextClass(crawlability_score)}`}>
							{crawlability_score.toFixed(1)}
							<span className="bot-crawlability__score-max">/ 100</span>
						</p>
					</div>
					<div className="bot-crawlability__score-meta">
						<p>Robots.txt: {robots_txt_present ? "✓" : "✗"}</p>
						<p>Sitemap: {sitemap_present ? "✓" : "✗"}</p>
					</div>
				</div>
			</Card>

			{indexabilityData && (
				<div className="bot-crawlability__indexability">
					<IndexabilityWarning data={indexabilityData} />
				</div>
			)}

			{default_rule !== "none" && (
				<div
					className={`bot-crawlability__default-rule bot-crawlability__default-rule--${
						default_rule === "allow" ? "allow" : "block"
					}`}
				>
					<p className="bot-crawlability__default-rule-title">
						Default Rule: User-agent: * →{" "}
						{default_rule === "allow" ? "Allow: /" : "Disallow: /"}
					</p>
					<p className="bot-crawlability__default-rule-text">
						{default_rule === "allow"
							? "Bots without explicit rules inherit this permissive default."
							: "⚠️ WARNING: This blocks ALL unlisted bots by default!"}
					</p>
				</div>
			)}

			{missingExplicitRules.length > 0 && (
				<Alert variant="warning" className="bot-crawlability__advisory">
					<AlertCircle className="bot-crawlability__alert-icon" />
					<div className="bot-crawlability__alert-body">
						<p className="bot-crawlability__alert-title">
							Advisory: {missingExplicitRules.length} major AI{" "}
							{missingExplicitRules.length === 1 ? "bot is" : "bots are"} not explicitly listed
						</p>
						<p className="bot-crawlability__alert-text">
							While {missingExplicitRules.map((b) => botDisplayNames[b]).join(", ")} currently
							inherit the default Allow rule, explicitly allowlisting major AI crawlers
							future-proofs your site against accidental blocks.
						</p>
						<div className="bot-crawlability__code-block">
							<button
								type="button"
								onClick={() => {
									const code = missingExplicitRules
										.map((bot) => `User-agent: ${bot}\nAllow: /`)
										.join("\n\n");
									navigator.clipboard.writeText(code);
								}}
								className="bot-crawlability__copy-btn"
							>
								Copy
							</button>
							<p className="bot-crawlability__code-label">
								Recommended addition to robots.txt:
							</p>
							<pre className="bot-crawlability__code">
								{missingExplicitRules.map((bot) => `User-agent: ${bot}\nAllow: /`).join("\n\n")}
							</pre>
						</div>
					</div>
				</Alert>
			)}

			{blockedBots.length > 0 && (
				<Alert variant="destructive" className="bot-crawlability__blocked-alert">
					<AlertCircle className="bot-crawlability__alert-icon" />
					<div className="bot-crawlability__alert-body">
						<p className="bot-crawlability__alert-title">
							{blockedBots.length} major AI {blockedBots.length === 1 ? "bot is" : "bots are"}{" "}
							blocked
						</p>
						<p className="bot-crawlability__alert-text">
							Blocked: <strong>{blockedBots.join(", ")}</strong>
						</p>
						<div className="bot-crawlability__code-block bot-crawlability__code-block--danger">
							<p className="bot-crawlability__code-label">Add to robots.txt:</p>
							<pre className="bot-crawlability__code">
								{blockedBots.map((bot) => getRobotsTxtFix(bot)).join("\n\n")}
							</pre>
						</div>
					</div>
				</Alert>
			)}

			<div className="bot-crawlability__bots">
				<h3 className="bot-crawlability__section-title">AI Bot Access Status</h3>
				<div className="bot-crawlability__bots-grid">
					{Object.entries(bot_access).map(([bot, botInfo]) => {
						const citationRate = getCitationRate(bot);
						return (
							<div key={bot} className="bot-crawlability__bot-card">
								<div className="bot-crawlability__bot-card-header">
									<span className="bot-crawlability__bot-name">
										{botDisplayNames[bot] || bot}
									</span>
									{getBotStatusBadge(botInfo)}
								</div>
								{citationRate !== null && (
									<div className="bot-crawlability__bot-citation">
										<span className={`bot-crawlability__bot-citation-rate ${getCitationRateClass(citationRate)}`}>
											Cited {citationRate}%
										</span>
									</div>
								)}
							</div>
						);
					})}
				</div>
			</div>

			<div className="bot-crawlability__sitemap">
				<FileText className="bot-crawlability__sitemap-icon" />
				<div className="bot-crawlability__sitemap-body">
					<p className="bot-crawlability__sitemap-title">Sitemap Status</p>
					{sitemap_present ? (
						<div>
							<p className="bot-crawlability__sitemap-found">✓ Sitemap found</p>
							{sitemap_url && (
								<a
									href={sitemap_url}
									target="_blank"
									rel="noopener noreferrer"
									className="bot-crawlability__sitemap-link"
								>
									{sitemap_url}
								</a>
							)}
						</div>
					) : (
						<div>
							<p className="bot-crawlability__sitemap-missing">⚠ No sitemap detected</p>
							<p className="bot-crawlability__sitemap-hint">
								Add a sitemap.xml file to help AI crawlers discover your content
							</p>
						</div>
					)}
				</div>
			</div>

			{raw_robots_txt && (
				<Collapsible open={showRobotsTxt} onOpenChange={setShowRobotsTxt}>
					<CollapsibleTrigger className="bot-crawlability__robots-trigger">
						<ChevronDown
							className={`bot-crawlability__robots-chevron${
								showRobotsTxt ? " bot-crawlability__robots-chevron--open" : ""
							}`}
						/>
						View robots.txt
					</CollapsibleTrigger>
					<CollapsibleContent>
						<div className="bot-crawlability__robots-viewer">
							<pre className="bot-crawlability__robots-pre">
								{raw_robots_txt.split("\n").map((line, idx) => {
									const isAIBot = [
										"GPTBot",
										"ClaudeBot",
										"PerplexityBot",
										"Googlebot",
										"anthropic-ai",
										"cohere-ai",
										"Bytespider",
									].some((botName) => line.toLowerCase().includes(botName.toLowerCase()));

									return (
										<div
											key={idx}
											className={
												isAIBot ? "bot-crawlability__robots-line--highlight" : ""
											}
										>
											{line || "\n"}
										</div>
									);
								})}
							</pre>
							{!["GPTBot", "ClaudeBot", "PerplexityBot"].some((bot) =>
								raw_robots_txt.toLowerCase().includes(bot.toLowerCase())
							) && (
								<p className="bot-crawlability__robots-note">
									⚠️ No AI-specific user-agent rules found. Bots are governed by the
									User-agent: * default rule.
								</p>
							)}
						</div>
					</CollapsibleContent>
				</Collapsible>
			)}
		</motion.div>
	);
}
